import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  FiHome,
  FiShoppingCart,
  FiCoffee,
  FiUsers,
  FiFileText,
  FiSettings,
  FiHelpCircle,
  FiBarChart2,
  FiUserCheck,
  FiTable,
  FiBook,
  FiGrid,
  FiPackage,
  FiAward,
  FiBox,
  FiEdit,
  FiDollarSign,
  FiShoppingBag,
  FiTrendingUp,
  FiPieChart,
  FiMenu,
  FiX,
  FiChevronDown,
  FiChevronRight,
} from 'react-icons/fi';
import { FaUtensils } from 'react-icons/fa';
import logo from '../../../../assets/elegantelogo.png';

// Modern Material-UI styled Sidebar
const Sidebar = ({ activeMenu, setActiveMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({
    menu: false,
    inventory: false,
    account: false,
  });

  // Get current path for active state
  const currentPath = location.pathname;

  // Auto-expand submenus based on current path
  useEffect(() => {
    const path = currentPath.toLowerCase();
    const newExpanded = { ...expandedMenus };
    
    if (path.includes('/menu') || path.includes('/categories') || path.includes('/items')) {
      newExpanded.menu = true;
    }
    if (path.includes('/inventory') || path.includes('/measurement') || path.includes('/stocks') || path.includes('/recipe')) {
      newExpanded.inventory = true;
    }
    if (path.includes('/account') || path.includes('/purchase')) {
      newExpanded.account = true;
    }
    
    setExpandedMenus(newExpanded);
  }, [currentPath]);

  const toggleMenu = (menu) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const handleNavigation = (menuName, path) => {
    if (setActiveMenu) {
      setActiveMenu(menuName);
    }
    if (path && path !== '#') {
      navigate(path);
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Check if a menu item is active
  const isActive = (path, exact = false) => {
    if (!path || path === '#') return false;
    if (exact) {
      return currentPath === path;
    }
    return currentPath.startsWith(path) || currentPath === path;
  };

  // Check if any subitem is active
  const isSubmenuActive = (subItems) => {
    if (!subItems) return false;
    return subItems.some(item => isActive(item.path));
  };

  const menuItems = [
    {
      name: 'Dashboard',
      icon: <FiBarChart2 />,
      path: '/ManagerDashboard_L',
      exact: true,
    },
    {
      name: 'Manage Approvals',
      icon: <FiUserCheck />,
      path: '/Lounge/LoungeDiscountManager',
      exact: true,
    },
    {
      name: 'Table',
      icon: <FiTable />,
      path: '/LoungeTables',
      exact: true,
    },
    {
      name: 'Menu',
      icon: <FiBook />,
      hasSubmenu: true,
      path: '#',
      subItems: [
        { name: 'Categories', icon: <FiGrid />, path: '/menu/LoungeCategories' },
        { name: 'Items', icon: <FaUtensils />, path: '/menu/LoungeMenuItems' },
      ],
    },
    {
      name: 'Inventory',
      icon: <FiPackage />,
     
    path: '/inventory/lounge',
    
    },
   
   
    {
      name: 'Report',
      icon: <FiPieChart />,
      path: '#',
    },
   
  ];

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
      }}
    >
      {/* Brand Header */}
      <Box
        sx={{
          py: 2.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          minHeight: 72,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Elegante Restaurant Logo"
          sx={{
            height: 44,
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Menu List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          py: 1,
          '&::-webkit-scrollbar': {
            width: 4,
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: 2,
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#d1d5db',
            borderRadius: 2,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#b0b8c4',
          },
        }}
      >
        {/* Section Label */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            px: 1.5,
            py: 1,
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            fontSize: '0.65rem',
          }}
        >
          Main
        </Typography>

        <List sx={{ py: 0 }}>
          {menuItems.map((item) => {
            const isItemActive = isActive(item.path, item.exact);
            const isSubActive = item.hasSubmenu && isSubmenuActive(item.subItems);
            const isExpanded = expandedMenus[item.name.toLowerCase()] || isSubActive;

            if (item.hasSubmenu) {
              return (
                <Box key={item.name}>
                  <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      onClick={() => {
                        // Only toggle the submenu, don't navigate
                        toggleMenu(item.name.toLowerCase());
                        // Optionally navigate to the parent path if it's not '#'
                        if (item.path && item.path !== '#') {
                          navigate(item.path);
                        }
                      }}
                      selected={isItemActive || isSubActive}
                      sx={{
                        borderRadius: 1.5,
                        mb: 0.25,
                        minHeight: 44,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(198, 40, 40, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(198, 40, 40, 0.12)',
                          },
                          '& .MuiListItemIcon-root': {
                            color: '#C62828',
                          },
                          '& .MuiListItemText-primary': {
                            color: '#C62828',
                            fontWeight: 700,
                          },
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: isItemActive || isSubActive ? '#C62828' : 'text.secondary',
                          fontSize: 20,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: isItemActive || isSubActive ? 700 : 600,
                          color: isItemActive || isSubActive ? '#C62828' : 'text.primary',
                          noWrap: true,
                        }}
                      />
                      {isExpanded ? (
                        <FiChevronDown size={16} style={{ color: '#b0b8c4' }} />
                      ) : (
                        <FiChevronRight size={16} style={{ color: '#b0b8c4' }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 2 }}>
                      {item.subItems.map((subItem) => {
                        const isSubItemActive = isActive(subItem.path, true);
                        return (
                          <ListItem key={subItem.name} disablePadding>
                            <ListItemButton
                              onClick={() => handleNavigation(subItem.name, subItem.path)}
                              selected={isSubItemActive}
                              sx={{
                                borderRadius: 1.5,
                                pl: 5,
                                minHeight: 36,
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(198, 40, 40, 0.06)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(198, 40, 40, 0.10)',
                                  },
                                  '& .MuiListItemIcon-root': {
                                    color: '#C62828',
                                  },
                                  '& .MuiListItemText-primary': {
                                    color: '#C62828',
                                    fontWeight: 600,
                                  },
                                },
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 32,
                                  color: isSubItemActive ? '#C62828' : 'text.secondary',
                                  fontSize: 16,
                                }}
                              >
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.name}
                                primaryTypographyProps={{
                                  fontSize: 13,
                                  fontWeight: isSubItemActive ? 600 : 500,
                                  color: isSubItemActive ? '#C62828' : 'text.secondary',
                                  noWrap: true,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            return (
              <ListItem key={item.name} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.name, item.path)}
                  selected={isItemActive}
                  disabled={item.path === '#'}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.25,
                    minHeight: 44,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(198, 40, 40, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(198, 40, 40, 0.12)',
                      },
                      '& .MuiListItemIcon-root': {
                        color: '#C62828',
                      },
                      '& .MuiListItemText-primary': {
                        color: '#C62828',
                        fontWeight: 700,
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: isItemActive ? '#C62828' : 'text.secondary',
                      fontSize: 20,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: isItemActive ? 700 : 600,
                      color: isItemActive ? '#C62828' : 'text.primary',
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 1 }} />

        {/* Support Section */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            px: 1.5,
            py: 0.5,
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            fontSize: '0.65rem',
          }}
        >
          Support
        </Typography>

        <List sx={{ py: 0 }}>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={() => navigate('/help')}
              sx={{
                borderRadius: 1.5,
                mb: 0.25,
                minHeight: 44,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: 'text.secondary',
                  fontSize: 20,
                }}
              >
                <FiHelpCircle />
              </ListItemIcon>
              <ListItemText
                primary="Help"
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'text.primary',
                  noWrap: true,
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Hamburger Button */}
      <IconButton
        onClick={handleDrawerToggle}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1200,
          bgcolor: 'background.paper',
          boxShadow: 2,
          borderRadius: 2,
          '&:hover': {
            bgcolor: 'background.paper',
          },
        }}
      >
        <FiMenu size={22} />
      </IconButton>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            borderRight: 'none',
            boxShadow: '0 0 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 260,
            borderRight: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#ffffff',
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflow: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;